import {
  HttpClient,
  HttpContext,
  HttpHeaders,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, race, throwError, timer } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
  SKIP_ERROR_TOAST,
  SKIP_LOADING,
} from "../interceptors/http-context";

const HTTP_TIMEOUT_MS = 30000;

export interface ApiRequestOptions {
  skipLoading?: boolean;
  skipErrorToast?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private headers = new HttpHeaders({ "Content-Type": "application/json" });
  constructor(private http: HttpClient) {}

  private buildContext(options?: ApiRequestOptions): HttpContext {
    return new HttpContext()
      .set(SKIP_LOADING, options?.skipLoading ?? false)
      .set(SKIP_ERROR_TOAST, options?.skipErrorToast ?? false);
  }

  private withTimeout<T>(observable: Observable<T>): Observable<T> {
    return race(
      observable,
      timer(HTTP_TIMEOUT_MS).pipe(
        switchMap(() =>
          throwError(
            () => new Error("A requisição excedeu o tempo limite."),
          ),
        ),
      ),
    ).pipe(catchError((error) => throwError(() => error)));
  }

  private getApiBaseUrl(): string {
    const baseUrl = environment.apiBaseUrl.endsWith("/")
      ? environment.apiBaseUrl
      : `${environment.apiBaseUrl}/`;
    const apiPathPrefix = (environment.apiPathPrefix || "api").replace(
      /^\/+|\/+$/g,
      "",
    );

    return `${baseUrl}${apiPathPrefix}/`;
  }

  get(
    domain: string,
    param?: any,
    options?: ApiRequestOptions,
  ): Observable<any> {
    let url = this.getApiBaseUrl() + domain;
    if (param) {
      url += "/" + param;
    }

    return this.withTimeout(
      this.http.get(url, {
        headers: this.headers,
        context: this.buildContext(options),
      }),
    );
  }

  post(
    domain: string,
    data: any,
    options?: ApiRequestOptions,
  ): Observable<any> {
    const url = this.getApiBaseUrl() + domain;

    return this.withTimeout(
      this.http.post(url, JSON.stringify(data), {
        headers: this.headers,
        context: this.buildContext(options),
      }),
    );
  }

  put(
    domain: string,
    data: any,
    options?: ApiRequestOptions,
  ): Observable<any> {
    const url = this.getApiBaseUrl() + domain;

    return this.withTimeout(
      this.http.put(url, JSON.stringify(data), {
        headers: this.headers,
        observe: "response",
        context: this.buildContext(options),
      }),
    );
  }

  delete(
    domain: string,
    guid: string,
    options?: ApiRequestOptions,
  ): Observable<any> {
    const url = this.getApiBaseUrl() + domain + "/" + guid;

    return this.withTimeout(
      this.http.delete(url, {
        headers: this.headers,
        context: this.buildContext(options),
      }),
    );
  }
}
